import type { CallOutcomeExtra, CallOutcomeOption } from './Softphone';
import type { SoftphoneStatus } from './softphoneClient.types';
import { ESC_PRIORITY, useEscStack } from '../contexts/EscCancelable.context';
import { FloatingWidget } from '../FloatingWidget/FloatingWidget';
import { ChangeDialNumberPopup } from './ChangeDialNumberPopup';
import { Softphone } from './Softphone';

export interface SoftphoneDialerPureProps {
  /** Contact display info for the dialer card. */
  contactName?: string;
  phoneNumber: string;
  imageSrc?: string | null;
  /** The dialed number is the contact's org ("firm") number — surfaces a "Firm number" badge. */
  isFirmNumber?: boolean;

  // Softphone passthrough (the per-surface provider owns this state via useSoftphone).
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  isRecording?: boolean;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  /** Send a DTMF digit on the live call. Undefined = keypad hidden. */
  onSendDigits?: (digits: string) => void;
  onSelectOutcome: (value: string, extra?: CallOutcomeExtra) => void;
  /** Close the dialer (ends any live call — the providers' close semantics). */
  onClose: () => void;

  // Sticky dialing-number passthrough (the provider owns the dial-info query + mutation).
  /** The contact's pinned from-number (or the in-flight call's number). */
  callerNumber?: string | null;
  /** The pinned number is no longer in the rep's bucket — warn, never auto-change. */
  callerNumberStale?: boolean;
  /** The rep's assigned numbers; the Change affordance only shows when an alternative exists. */
  bucketNumbers?: string[];
  changeNumberOpen?: boolean;
  changeNumberSaving?: boolean;
  /** Open the guarded change-number confirmation. Undefined = affordance hidden entirely. */
  onOpenChangeNumber?: () => void;
  onCloseChangeNumber?: () => void;
  onConfirmChangeNumber?: (phoneNumber: string) => void;
}

/**
 * Pure, presentational floating dialer — the draggable `FloatingWidget` card the per-surface
 * `SoftphoneProvider` mounts ONCE, above the app, so a live call survives page/tab navigation.
 * No tRPC, no softphone client: the provider owns the network + WebRTC and feeds this its
 * controller state. Keeping the view pure lets it be component-tested without the carrier SDKs.
 *
 * Rendering this component IS "the dialer is open" — the provider only mounts it while a call
 * session is active. The team/phone gates (and the "already on a call" guard) live upstream on
 * the `ClickToCallButtonPure` trigger, so this component never has to decide whether to show.
 */
export function SoftphoneDialerPure({
  contactName,
  phoneNumber,
  imageSrc,
  isFirmNumber,
  status,
  durationSec,
  isMuted,
  error,
  isRecording,
  outcomes,
  onToggleMute,
  onHangup,
  onSendDigits,
  onSelectOutcome,
  onClose,
  callerNumber,
  callerNumberStale,
  bucketNumbers,
  changeNumberOpen,
  changeNumberSaving,
  onOpenChangeNumber,
  onCloseChangeNumber,
  onConfirmChangeNumber,
}: SoftphoneDialerPureProps) {
  const inCall = status === 'connecting' || status === 'ringing' || status === 'active';

  // ESC dismisses an idle/ended/error dialer (e.g. the outcome screen) but must NEVER hang up a
  // live call — the old modal's backdrop/ESC-close silently dropped calls. LOW priority so any
  // real popup stacked above (ChangeDialNumberPopup registers at INTERSTITIAL) wins ESC first.
  useEscStack('SOFTPHONE_FLOAT', ESC_PRIORITY.LOW, inCall ? undefined : onClose);

  return (
    <>
      <FloatingWidget
        onClose={onClose}
        closeTooltip={inCall ? 'End call and close' : 'Close'}
        data-test="softphone-widget">
        <Softphone
          contactName={contactName}
          phoneNumber={phoneNumber}
          isFirmNumber={isFirmNumber}
          status={status}
          durationSec={durationSec}
          isMuted={isMuted}
          error={error}
          isRecording={isRecording}
          imageSrc={imageSrc}
          callerNumber={callerNumber}
          callerNumberStale={callerNumberStale}
          // Offer the guarded Change flow only when there's actually another number to pick.
          onChangeNumber={bucketNumbers?.some((n) => n !== callerNumber) ? onOpenChangeNumber : undefined}
          outcomes={outcomes}
          onToggleMute={onToggleMute}
          onHangup={onHangup}
          onSendDigits={onSendDigits}
          onSelectOutcome={onSelectOutcome}
          onClose={onClose}
          // FloatingWidget owns the card chrome (rounded corners, shadow); the handle strip
          // above the card supplies the top spacing.
          className="pt-2"
        />
      </FloatingWidget>

      {changeNumberOpen && onCloseChangeNumber && onConfirmChangeNumber && (
        <ChangeDialNumberPopup
          contactName={contactName}
          currentNumber={callerNumber ?? null}
          bucketNumbers={bucketNumbers ?? []}
          saving={changeNumberSaving}
          onConfirm={onConfirmChangeNumber}
          onClose={onCloseChangeNumber}
        />
      )}
    </>
  );
}
